import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@/shared/icons'
import { Tooltip } from '@/shared/components/ui';
interface MessageSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onSearchChange: (query: string) => void;
  totalResults?: number;
  currentResultIndex?: number;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  /** Valor inicial para pré-preencher a busca (vindo da sidebar) */
  initialValue?: string;
}

/**
 * Componente de busca de mensagens
 *
 * @example
 * <MessageSearch
 *   isOpen={isSearchOpen}
 *   onClose={() => setIsSearchOpen(false)}
 *   onSearchChange={handleSearch}
 *   totalResults={10}
 *   currentResultIndex={0}
 *   onNavigateNext={handleNext}
 *   onNavigatePrev={handlePrev}
 * />
 */
const MessageSearch: React.FC<MessageSearchProps> = ({
  isOpen,
  onClose,
  onSearchChange,
  totalResults = 0,
  currentResultIndex = 0,
  onNavigateNext,
  onNavigatePrev,
  initialValue,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // ✅ Sincronizar com initialValue quando fornecido (busca vinda da sidebar)
  useEffect(() => {
    if (initialValue && initialValue.trim()) {
      setSearchQuery(initialValue.trim());
    }
  }, [initialValue]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, onSearchChange]);

  const handleClose = () => {
    setSearchQuery('');
    onSearchChange('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    } else if (e.key === 'Enter') {
      if (e.shiftKey) {
        onNavigatePrev?.();
      } else {
        onNavigateNext?.();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="border-b px-4 py-3"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: 'var(--theme-card-background)'
      }}
      data-testid="message-search"
    >
      <div className="flex items-center gap-2">
        {/* FiSearch Icon */}
        <Icon name="search" className="w-5 h-5 flex-shrink-0"
          style={{ color: 'var(--theme-muted-text)' }} />

        {/* FiSearch Input */}
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar mensagens…"
          className="flex-1 h-[38px] outline-none text-sm bg-transparent"
          style={{ color: 'var(--theme-card-text)' }}
          data-testid="message-search-input"
        />

        {/* Results Counter */}
        {searchQuery && (
          <div
            className="flex items-center gap-2 text-sm"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            {totalResults > 0 ? (
              <>
                <span className="font-medium">
                  {currentResultIndex + 1} de {totalResults}
                </span>

                {/* Navigation Buttons — Tooltip shared (F80, title= nativo some) */}
                <div className="flex items-center gap-1">
                  <Tooltip content="Anterior (Shift+Enter)">
                    <button
                      onClick={onNavigatePrev}
                      disabled={totalResults === 0 || currentResultIndex === 0}
                      className="p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ color: 'var(--theme-sidebar-text)' }}
                      onMouseEnter={(e) => {
                        if (totalResults > 0 && currentResultIndex > 0) {
                          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      aria-label="Anterior (Shift+Enter)"
                      data-testid="message-search-prev"
                    >
                      <Icon name="chevron-up" className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Próximo (Enter)">
                    <button
                      onClick={onNavigateNext}
                      disabled={totalResults === 0 || currentResultIndex >= totalResults - 1}
                      className="p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ color: 'var(--theme-sidebar-text)' }}
                      onMouseEnter={(e) => {
                        if (totalResults > 0 && currentResultIndex < totalResults - 1) {
                          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      aria-label="Próximo (Enter)"
                      data-testid="message-search-next"
                    >
                      <Icon name="chevron-down" className="w-4 h-4" />
                    </button>
                  </Tooltip>
                </div>
              </>
            ) : (
              <span>Nenhum resultado</span>
            )}
          </div>
        )}

        {/* Close Button — Tooltip shared (F80) */}
        <Tooltip content="Fechar (Esc)">
          <button
            onClick={handleClose}
            className="p-1 rounded flex-shrink-0 transition-colors"
            style={{ color: 'var(--theme-sidebar-text)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Fechar (Esc)"
            data-testid="message-search-close"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </Tooltip>
      </div>

      {/* FiSearch Tips */}
      {searchQuery === '' && (
        <div
          className="mt-2 text-xs"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Dica: Use Enter para navegar entre resultados, Shift+Enter para voltar
        </div>
      )}
    </div>
  );
};

export default MessageSearch;
